import { TaskSubTicketResource } from 'dy-frontend-http-repository/lib/modules/TaskSubTicket/resources';
import { TaskSubTicketStatus } from 'dy-frontend-http-repository/lib/modules/TaskSubTicket/resources/TaskSubTicketResource';
import { useStore } from 'effector-react';
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import ConfirmationPopover from '@app/components/ConfirmationPopover';
import { openModal } from '@app/containers/modals/store/events';
import { $authorizedUser, $permissions } from '@app/containers/store/states';
import { taskSubTicketStatusInformation } from '@app/data/consts';
import { usePageTitle } from '@app/hooks';
import { Button, Classes, Icon, Intent, Spinner, Switch, Tag } from '@blueprintjs/core';
import Flex from '@components/Flex';
import Heading from '@components/Heading';
import NonIdealState from '@components/NonIdealState';
import PublishDateRangeFilter from '@components/PublishDateRangeFilter';

import { $task } from '../../store/states';
import CreateWorkReportModal, { CreateWorkReportModalProps } from '../TaskWorkReports/modals/CreateWorkReportModal';

import ClosedParentNotice from './components/ClosedParentNotice';
import DesignersFilter from './components/DesignersFilter';
import DesignerWorkspace from './components/DesignerWorkspace';
import DraggableSubTicketRow from './components/DraggableSubTicketRow';
import SubTicketDetailPanel from './components/SubTicketDetailPanel';
import SubTicketListItem from './components/SubTicketListItem';
import CreateSubTicketModal, { CreateSubTicketModalProps } from './modals/CreateSubTicketModal';
import UpdateSubTicketModal, { UpdateSubTicketModalProps } from './modals/UpdateSubTicketModal';
import { subTicketListApi } from './store/apis';
import {
    archiveSubTicket,
    fetchActiveTimerSession,
    fetchSubTicketDetail,
    fetchSubTickets,
    reorderSubTicketList,
    restoreSubTicket,
} from './store/effects';
import {
    resetSubTickets,
    setDesignerFilter,
    setDueDateRange,
    setExpandedSubTicketId,
    setIsArchivedShown,
    setIsOnlyMineShown,
    setStatusFilter,
} from './store/events';
import {
    SubTicketRecoveryScope,
    reconcileSubTicketRef,
    refreshSubTicketViews,
    reportSubTicketMutation,
    showSubTicketFailure,
} from './store/reconcile';
import {
    ACTIVE_SESSION_KEY,
    claimScope,
    detailKey,
    invalidateScope,
    listKey,
    nextGeneration,
    releaseScope,
} from './store/requestGeneration';
import { issueRunningTimerDetail } from './store/runningTimerDetail';
import {
    $activeTimerSession,
    $designerFilter,
    $dueDateRange,
    $expandedSubTicketId,
    $isArchivedShown,
    $isOnlyMineShown,
    $statusFilter,
    $subTicketDetailStates,
    $subTicketList,
    selectSubTicketRows,
} from './store/states';

import {
    filterVisibleSubTickets,
    isSubTicketReadSettledFor,
    moveSubTicketToIndex,
    resolveClosedParentNotice,
    resolveDesignerFilterOptions,
    resolveRunningTimerView,
    resolveSubTicketCapabilities,
    resolveSubTicketConflict,
} from './utils';

const FILTERABLE_STATUSES: TaskSubTicketStatus[] = ['to_design', 'in_progress', 'in_revision', 'complete'];

const TaskSubTickets: React.FC = () => {
    const { changeTitle } = usePageTitle('Sub Tasks');

    const location = useLocation();

    const permissions = useStore($permissions);
    const me = useStore($authorizedUser);
    const task = useStore($task);
    const subTicketList = useStore($subTicketList);
    const subTicketDetailStates = useStore($subTicketDetailStates);
    const activeTimerSession = useStore($activeTimerSession);
    const isArchivedShown = useStore($isArchivedShown);
    const isOnlyMineShown = useStore($isOnlyMineShown);
    const statusFilter = useStore($statusFilter);
    const designerFilter = useStore($designerFilter);
    const dueDateRange = useStore($dueDateRange);
    const expandedSubTicketId = useStore($expandedSubTicketId);

    // A single clock for the whole list: every row derives "overdue" from the same instant
    const [nowMs, setNowMs] = useState(() => Date.now());
    const [mutatingSubTicketId, setMutatingSubTicketId] = useState<ID | null>(null);
    const [isReordering, setIsReordering] = useState(false);

    // The order under the hand while a drag is in flight. Null means "whatever the server last
    // said", which is what every read and every refresh restores.
    const [draftOrder, setDraftOrder] = useState<ID[] | null>(null);
    const draftOrderRef = useRef<ID[] | null>(null);

    // Where the aside's "View all" aims
    const subTicketsHeaderRef = useRef<HTMLDivElement | null>(null);

    const isModuleEnabled = permissions.isEnabled.taskSubTicket || permissions.isRoot.taskSubTicket;

    // Ownership is claimed BEFORE any read is issued and released on teardown. Everything the
    // page reads is guarded against it, so a mutation refresh belonging to the Request the user
    // just left cannot mark this one unsettled or repopulate it. Claiming in a layout effect
    // rather than a passive one closes the gap in which a render could issue a read under the
    // previous owner.
    useLayoutEffect(() => {
        if (!task || !isModuleEnabled) {
            return;
        }

        claimScope('page', task.id);
    }, [task?.id, isModuleEnabled]);

    useEffect(() => {
        if (!task || !isModuleEnabled) {
            return;
        }

        fetchSubTickets({
            filter: { task_id: task.id, include_archived: isArchivedShown ? '1' : '0' },
            generation: nextGeneration(listKey(task.id)),
        }).catch((e) => {
            showSubTicketFailure('Sub-tasks could not be loaded. Please refresh the page.');
            console.error(e);
        });
    }, [task?.id, isModuleEnabled, isArchivedShown]);

    // The open session is global to the designer, not to this Request, so it is fetched once
    // per mount and restored entirely from the server
    useEffect(() => {
        if (!isModuleEnabled) {
            return;
        }

        fetchActiveTimerSession({ generation: nextGeneration(ACTIVE_SESSION_KEY) }).catch((e) => {
            console.error(e);
        });
    }, [isModuleEnabled]);

    useEffect(() => {
        if (!task) {
            return;
        }

        changeTitle(`Sub Tasks - ${task.title}`);
    }, [task?.title]);

    useEffect(() => {
        const intervalId = window.setInterval(() => setNowMs(Date.now()), 1000);

        return () => {
            window.clearInterval(intervalId);
        };
    }, []);

    useEffect(() => {
        return () => {
            // Only the page's own scope is invalidated. The aside outlives this route, so its
            // summary — including a mutation-triggered refresh issued moments ago — must not be
            // cancelled just because the Sub Tasks tab was switched away from.
            invalidateScope('page');
            releaseScope('page');
            resetSubTickets();
        };
    }, []);

    // Rows belong to the Request the read that produced them was issued for, and the store hands
    // them over only when asked for that Request by name
    const currentRows = useMemo(
        () => (task ? selectSubTicketRows(subTicketList, task.id) : null),
        [subTicketList, task?.id]
    );

    // Filters are applied locally on top of the archived filter the server already applied, so
    // toggling a chip is instant and cannot race a request
    const visibleSubTickets = useMemo(() => {
        if (!currentRows || !me) {
            return [];
        }

        return filterVisibleSubTickets({
            subTickets: currentRows,
            currentUserId: me.user.id,
            isArchivedShown,
            isOnlyMineShown,
            statusFilter,
            designerFilter,
            dueFromDate: dueDateRange.fromDate,
            dueToDate: dueDateRange.toDate,
        });
    }, [
        currentRows,
        me,
        isArchivedShown,
        isOnlyMineShown,
        statusFilter,
        designerFilter,
        dueDateRange.fromDate,
        dueDateRange.toDate,
    ]);

    // Offered from the rows the Request LOADED, never from the rows currently shown: options
    // that vanish the moment they are used cannot be unpicked from their own popover
    const designerFilterOptions = useMemo(() => resolveDesignerFilterOptions(currentRows), [currentRows]);

    // What is actually rendered: the server's order, or the draft the hand is currently holding.
    // A row the draft has never heard of — one that arrived from a refresh mid-drag — keeps its
    // place at the end rather than disappearing.
    const orderedSubTickets = useMemo(() => {
        if (!draftOrder) {
            return visibleSubTickets;
        }

        const byId = new Map(visibleSubTickets.map((row) => [row.id, row]));

        const drafted = draftOrder
            .map((id) => byId.get(id))
            .filter((row): row is TaskSubTicketResource => row !== undefined);
        const rest = visibleSubTickets.filter((row) => !draftOrder.includes(row.id));

        return [...drafted, ...rest];
    }, [visibleSubTickets, draftOrder]);

    // One owner for the running-timer label AND for the single detail read that may back it.
    //
    // The decision is a pure function over the active session, the list read state and the
    // details already known, so both arrival orders — active session first, list first — are
    // provable without mounting anything. Crucially it waits for the list to SETTLE for this
    // Request before treating "absent from the list" as a reason to fetch anything.
    const runningTimerView = useMemo(
        () =>
            task ?
                resolveRunningTimerView({
                    taskId: task.id,
                    activeSession: activeTimerSession,
                    listRead: subTicketList,
                    rows: currentRows,
                    detailStates: subTicketDetailStates,
                })
            :   { label: null, taskId: null, fetchDetailId: null },
        [task?.id, activeTimerSession, subTicketList, currentRows, subTicketDetailStates]
    );

    // Sole owner of the running-elsewhere detail read
    useEffect(() => {
        issueRunningTimerDetail(runningTimerView, activeTimerSession);
    }, [runningTimerView.fetchDetailId, activeTimerSession?.task_id]);

    // "View all" in the aside asks for this explicitly, and only it does — nothing else on the
    // page moves the viewport, so an ordinary tab switch still lands wherever the reader was.
    //
    // Keyed on `location.key` rather than on the pathname: clicking "View all" while already on
    // the Sub Tasks tab does not change the route, but it does mint a new key, so the second
    // click brings the list back into view exactly like the first one did.
    useEffect(() => {
        const navigationState = location.state as { scrollToSubTickets?: boolean } | null;

        if (!navigationState?.scrollToSubTickets || !subTicketsHeaderRef.current) {
            return;
        }

        // Focus first, so a keyboard or screen-reader user is moved to the list rather than
        // being left at the top of the page while the page scrolls away underneath them
        subTicketsHeaderRef.current.focus();
        subTicketsHeaderRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, [location.key]);

    // Defence in depth: the tab is already filtered out without the module permission, but a
    // pasted URL reaches the route directly
    if (!isModuleEnabled) {
        return null;
    }

    if (!me || !task) {
        return null;
    }

    // Parent pause, delivery or archival closes the Request. Every new-write control is hidden
    // rather than disabled, matching the established isTaskDelivered idiom; the backend refuses
    // the same writes independently.
    //
    // Hiding alone leaves the page silently emptier than the designer expects, so the reason is
    // stated once at the top, naming which of the three states applies.
    const closedParentNotice = resolveClosedParentNotice({
        state: task.state,
        pausedAt: task.paused_at,
        archivedAt: task.archived_at,
    });

    const isParentClosed = closedParentNotice !== null;

    // The capability resolver takes the module-scoped root flag, not the whole container, so it
    // stays a pure function over a permission set rather than depending on the container shape
    const capabilityPermissions = {
        isRoot: permissions.isRoot.taskSubTicket,
        has: (permission: string) => permissions.has(permission),
    };

    const capabilitiesOf = (subTicket: TaskSubTicketResource) =>
        resolveSubTicketCapabilities({
            permissions: capabilityPermissions,
            isParentClosed,
            isAssignee: subTicket.assignee_user.id === me.user.id,
            isArchived: subTicket.archived_at !== null,
            // Authoritative global session, never the list row's `active_session` snapshot.
            // Stop & Save must appear the instant a Start succeeds — before any refresh, and
            // even if that refresh fails — and must survive the parent closing underneath it.
            hasRunningTimerHere: activeTimerSession?.task_sub_ticket_id === subTicket.id,
        });

    // Creation is not per-row, so it is resolved against a notional open phase
    const createCapabilities = resolveSubTicketCapabilities({
        permissions: capabilityPermissions,
        isParentClosed,
        isAssignee: false,
        isArchived: false,
        hasRunningTimerHere: false,
    });

    const refresh = () => refreshSubTicketViews({ taskId: task.id, isArchivedShown, expandedSubTicketId });

    // Every stale-read retry on this page repairs its target through the same shared handler
    const recoveryScope = (what: string): SubTicketRecoveryScope => ({ what, taskId: task.id, refreshViews: refresh });

    // Resolved once above, for the whole page: N rendered rows must not each decide for
    // themselves whether the running phase needs naming
    const runningElsewhereLabel = runningTimerView.label;
    const runningElsewhereTaskId = runningTimerView.taskId;

    const handleToggleExpanded = (subTicket: TaskSubTicketResource) => {
        const nextId = expandedSubTicketId === subTicket.id ? null : subTicket.id;
        setExpandedSubTicketId(nextId);

        if (nextId) {
            // Keyed by identity so this cannot settle, or be settled by, another phase's detail
            fetchSubTicketDetail({
                taskId: task.id,
                id: nextId,
                generation: nextGeneration(detailKey(task.id, nextId)),
            }).catch((e) => {
                showSubTicketFailure('The sub-task detail could not be loaded.');
                console.error(e);
            });
        }
    };

    const handleArchive = async (subTicket: TaskSubTicketResource) => {
        setMutatingSubTicketId(subTicket.id);

        try {
            const ref = await archiveSubTicket(subTicket.id);
            const reconciled = await reconcileSubTicketRef(ref, task.id);

            if (expandedSubTicketId === subTicket.id) {
                setExpandedSubTicketId(null);
            }

            // The archive committed. Drop the row now so that truth is visible while
            // "Show archived" is off; a refresh that then fails simply is not applied, so the
            // row cannot come back from the dead.
            if (!isArchivedShown) {
                subTicketListApi.remove({ taskId: task.id, subTicketId: subTicket.id });
            }

            const views = await refresh();

            reportSubTicketMutation({
                successMessage: `Sub-task #${subTicket.ordinal} archived.`,
                views,
                reconciled,
                scope: recoveryScope(`Sub-task #${subTicket.ordinal}`),
            });
        } catch (e) {
            const conflict = resolveSubTicketConflict(e, 'archive');
            showSubTicketFailure(conflict ? conflict.message : 'The sub-task could not be archived.');

            if (!conflict) {
                console.error(e);
            }
        } finally {
            setMutatingSubTicketId(null);
        }
    };

    const handleRestore = async (subTicket: TaskSubTicketResource) => {
        setMutatingSubTicketId(subTicket.id);

        try {
            const ref = await restoreSubTicket(subTicket.id);
            const reconciled = await reconcileSubTicketRef(ref, task.id);

            const views = await refresh();

            reportSubTicketMutation({
                successMessage: `Sub-task #${subTicket.ordinal} restored.`,
                views,
                reconciled,
                scope: recoveryScope(`Sub-task #${subTicket.ordinal}`),
            });
        } catch (e) {
            showSubTicketFailure('The sub-task could not be restored.');
            console.error(e);
        } finally {
            setMutatingSubTicketId(null);
        }
    };

    // Reordering submits the WHOLE non-archived set, which is what the endpoint requires and
    // what makes a stale client view a refusal rather than a half-applied order. Dragging is
    // therefore disabled whenever the visible list is not that whole set — under ANY active
    // filter, under "Only mine", or with archived rows shown — because a partial list could only
    // ever be rejected. Every filter added here has to join this list for the same reason.
    const isAnyFilterActive =
        statusFilter.length > 0 ||
        designerFilter.length > 0 ||
        dueDateRange.fromDate !== null ||
        dueDateRange.toDate !== null;

    const isOrderingAvailable =
        !isArchivedShown && !isOnlyMineShown && !isAnyFilterActive && visibleSubTickets.length >= 2;

    // Three independent reasons a row may not be dragged, and all three have to hold
    const canDragSubTicket = (subTicket: TaskSubTicketResource) =>
        isOrderingAvailable && capabilitiesOf(subTicket).canUpdate && !isReordering;

    // Hovering only ever moves the local draft. Nothing is sent until the row is released, so a
    // drag across ten rows is one request, not ten.
    const handleReorderHover = (draggedSubTicketId: ID, hoverIndex: number) => {
        const currentIds = orderedSubTickets.map((row) => row.id);
        const nextIds = moveSubTicketToIndex(currentIds, draggedSubTicketId, hoverIndex);

        if (nextIds === currentIds) {
            return;
        }

        // React may not render the final hover before the HTML5 backend fires `drop`. Keep the
        // latest order synchronously as well, so release always submits the position under the
        // pointer rather than the preceding render.
        draftOrderRef.current = nextIds;
        setDraftOrder(nextIds);
    };

    const handleReorderDrop = async () => {
        const orderedIds = draftOrderRef.current ?? visibleSubTickets.map((row) => row.id);
        const serverIds = visibleSubTickets.map((row) => row.id);

        // Dropped where it started: asking the endpoint to commit the order it already holds
        if (orderedIds.join(',') === serverIds.join(',')) {
            draftOrderRef.current = null;
            setDraftOrder(null);
            return;
        }

        setIsReordering(true);

        try {
            // The server returns the committed order; the refresh below re-reads it rather than
            // trusting the local draft, so the rows on screen are always the ones that were saved
            await reorderSubTicketList({ task_id: task.id, task_sub_ticket_ids: orderedIds });

            const views = await refresh();

            reportSubTicketMutation({
                successMessage: 'Sub-task order saved.',
                views,
                // A reorder touches no ref and no participation, so there is nothing to
                // reconcile — null keeps that distinct from "reconciled and found nothing stale"
                reconciled: null,
                scope: recoveryScope('Sub-task order'),
            });
        } catch (e) {
            showSubTicketFailure('The sub-tasks could not be reordered. Please reload and try again.');
            console.error(e);
        } finally {
            // Dropping the draft on both paths is what makes the server the only source of
            // order: on success the refresh above already holds the committed one, and on
            // failure the rows snap back to what was actually saved rather than lying
            draftOrderRef.current = null;
            setDraftOrder(null);
            setIsReordering(false);
        }
    };

    const handleReorderCancel = () => {
        draftOrderRef.current = null;
        setDraftOrder(null);
    };

    const handleReorderKeyboard = (draggedSubTicketId: ID, targetIndex: number) => {
        const currentIds = orderedSubTickets.map((row) => row.id);
        const nextIds = moveSubTicketToIndex(currentIds, draggedSubTicketId, targetIndex);

        if (nextIds === currentIds) {
            return;
        }

        draftOrderRef.current = nextIds;
        setDraftOrder(nextIds);
        void handleReorderDrop();
    };

    // Both the row's own logged total and the aside summary are server-derived, and the parent
    // state log is unaffected — a work report is not a status transition
    const handleWorkReportCreated = () => {
        refresh().catch((e) => console.error(e));
    };

    const renderActions = (subTicket: TaskSubTicketResource) => {
        const capabilities = capabilitiesOf(subTicket);
        const isMutating = mutatingSubTicketId === subTicket.id;

        const buttons: React.ReactNode[] = [];

        if (capabilities.canLogWork) {
            buttons.push(
                <Button
                    key="work-report"
                    className="mr-1"
                    icon="stopwatch"
                    disabled={isMutating}
                    onClick={() =>
                        openModal<CreateWorkReportModalProps>({
                            ModalComponent: CreateWorkReportModal,
                            data: {
                                taskId: task.id,
                                actorUserId: me.user.id,
                                subTicketId: subTicket.id,
                                subTicketOrdinal: subTicket.ordinal,
                                // The work belongs to the assigned designer even when a manager
                                // records it; the modal's Switch control still allows a change
                                subTicketAssignee: {
                                    id: subTicket.assignee_user.id,
                                    firstName: subTicket.assignee_user.first_name,
                                    lastName: subTicket.assignee_user.last_name,
                                    imageHash: subTicket.assignee_user.image_hash,
                                },
                                onCreated: handleWorkReportCreated,
                            },
                        })
                    }
                >
                    Work Report
                </Button>
            );
        }

        if (capabilities.canUpdate || capabilities.canManageAssignee) {
            buttons.push(
                <Button
                    key="update"
                    className="mr-1"
                    icon="edit"
                    disabled={isMutating}
                    onClick={() =>
                        openModal<UpdateSubTicketModalProps>({
                            ModalComponent: UpdateSubTicketModal,
                            data: {
                                subTicket,
                                canUpdate: capabilities.canUpdate,
                                canManageAssignee: capabilities.canManageAssignee,
                                isArchivedShown,
                                expandedSubTicketId,
                            },
                        })
                    }
                >
                    Update
                </Button>
            );
        }

        if (capabilities.canArchive) {
            buttons.push(
                <ConfirmationPopover
                    key="archive"
                    title="Are you sure want to archive this sub-task"
                    actions={[
                        <Button
                            intent={Intent.DANGER}
                            className={Classes.POPOVER_DISMISS}
                            onClick={() => handleArchive(subTicket)}
                        >
                            Yes, archive
                        </Button>,
                    ]}
                >
                    <Button loading={isMutating} icon="archive" intent={Intent.DANGER}>
                        Archive
                    </Button>
                </ConfirmationPopover>
            );
        }

        if (capabilities.canRestore) {
            buttons.push(
                <Button key="restore" loading={isMutating} icon="unarchive" onClick={() => handleRestore(subTicket)}>
                    Restore
                </Button>
            );
        }

        if (buttons.length === 0) {
            return null;
        }

        return (
            <Flex direction="row" align="center">
                {buttons}
            </Flex>
        );
    };

    const renderWorkspace = (subTicket: TaskSubTicketResource) => (
        <DesignerWorkspace
            subTicket={subTicket}
            capabilities={capabilitiesOf(subTicket)}
            isArchivedShown={isArchivedShown}
            expandedSubTicketId={expandedSubTicketId}
            runningElsewhereLabel={runningElsewhereLabel}
            runningElsewhereTaskId={runningElsewhereTaskId}
        />
    );

    const renderCreateButton = () => {
        if (!createCapabilities.canCreate) {
            return null;
        }

        return (
            <Button
                className="ml-auto"
                icon="add"
                intent={Intent.PRIMARY}
                onClick={() =>
                    openModal<CreateSubTicketModalProps>({
                        ModalComponent: CreateSubTicketModal,
                        data: { taskId: task.id, isArchivedShown },
                    })
                }
            >
                Create sub-task
            </Button>
        );
    };

    const renderStatusFilterChips = () => (
        <Flex direction="row" align="center" style={{ flexWrap: 'wrap' }}>
            {FILTERABLE_STATUSES.map((status) => {
                const isSelected = statusFilter.includes(status);
                const { color } = taskSubTicketStatusInformation[status];

                return (
                    <Tag
                        key={status}
                        className="mr-1"
                        interactive
                        // Selection is the only thing that fills here, so "which filters are on"
                        // is answerable at a glance. The shared chip helper cannot say that — it
                        // fills Complete by rule, which would read as a filter that is always on.
                        style={
                            isSelected ?
                                { background: color, color: '#FFFFFF', border: `1px solid ${color}` }
                            :   { background: 'transparent', color, border: `1px solid ${color}` }
                        }
                        onClick={() =>
                            setStatusFilter(
                                isSelected ? statusFilter.filter((s) => s !== status) : [...statusFilter, status]
                            )
                        }
                    >
                        {taskSubTicketStatusInformation[status].title}
                    </Tag>
                );
            })}
        </Flex>
    );

    const renderHeader = () => (
        // Focusable only programmatically: it is the landing point for "View all", never a stop
        // on the ordinary tab order
        <div className="mb-2" ref={subTicketsHeaderRef} tabIndex={-1} style={{ outline: 'none' }}>
            <Flex className="mb-1" direction="row" align="center" justify="space-between">
                <Flex direction="row" align="center">
                    <Heading className="mr-1" type="h4">
                        Sub Tasks
                    </Heading>
                    <Tag minimal round>
                        {currentRows ? currentRows.length : 0}
                    </Tag>
                </Flex>

                {renderCreateButton()}
            </Flex>

            <Flex direction="row" align="center" justify="space-between" style={{ flexWrap: 'wrap' }}>
                <Flex direction="row" align="center" style={{ flexWrap: 'wrap' }}>
                    {renderStatusFilterChips()}

                    {/* Designers, then Due Date — the two narrowing questions a lead asks of a
                        Request's phases, in the order they ask them. Both compose with the
                        status chips and with the two switches on the right; none of them is a
                        mode, so turning one off restores exactly the rows it was hiding */}
                    <div className="mr-1">
                        <DesignersFilter
                            options={designerFilterOptions}
                            selectedDesignerIds={designerFilter}
                            onApply={setDesignerFilter}
                        />
                    </div>

                    <PublishDateRangeFilter
                        title="Due Date"
                        fromDate={dueDateRange.fromDate}
                        toDate={dueDateRange.toDate}
                        onApply={(fromDate, toDate) => setDueDateRange({ fromDate, toDate })}
                    />
                </Flex>

                <Flex direction="row" align="center">
                    <Switch
                        className="mb-0 mr-2"
                        checked={isOnlyMineShown}
                        label="Only mine"
                        onChange={() => setIsOnlyMineShown(!isOnlyMineShown)}
                    />

                    <Switch
                        className="mb-0"
                        checked={isArchivedShown}
                        label="Show archived"
                        onChange={() => setIsArchivedShown(!isArchivedShown)}
                    />
                </Flex>
            </Flex>
        </div>
    );

    const renderSubTickets = () => {
        // Loading and "loaded and empty" are different answers and are shown differently. A
        // failed read is settled, so it falls through to the empty state rather than spinning
        // forever; the failure itself was already surfaced as a toast when the read rejected.
        if (!isSubTicketReadSettledFor(subTicketList, task.id)) {
            return (
                <Flex justify="center">
                    <Spinner />
                </Flex>
            );
        }

        if (visibleSubTickets.length === 0) {
            return (
                <NonIdealState
                    icon={<Icon className="mb-2" icon="layers" size={70} />}
                    title={
                        <Heading type="h4" className="mb-1">
                            No sub-tasks found
                        </Heading>
                    }
                />
            );
        }

        return orderedSubTickets.map((subTicket, index) => (
            <DraggableSubTicketRow
                key={subTicket.id}
                subTicketId={subTicket.id}
                ordinal={subTicket.ordinal}
                index={index}
                canDrag={canDragSubTicket(subTicket)}
                onHover={handleReorderHover}
                onDrop={handleReorderDrop}
                onCancel={handleReorderCancel}
                onKeyboardMove={handleReorderKeyboard}
                renderRow={(handle) => (
                    <SubTicketListItem
                        className="mb-2"
                        subTicket={subTicket}
                        nowMs={nowMs}
                        isExpanded={expandedSubTicketId === subTicket.id}
                        onToggleExpanded={() => handleToggleExpanded(subTicket)}
                        ordering={handle}
                        detail={
                            <SubTicketDetailPanel
                                state={subTicketDetailStates[subTicket.id] ?? null}
                                onRetry={() =>
                                    fetchSubTicketDetail({
                                        taskId: task.id,
                                        id: subTicket.id,
                                        generation: nextGeneration(detailKey(task.id, subTicket.id)),
                                    }).catch((e) => console.error(e))
                                }
                            />
                        }
                        actions={renderActions(subTicket)}
                        workspace={renderWorkspace(subTicket)}
                    />
                )}
            />
        ));
    };

    return (
        <div>
            <ClosedParentNotice className="mb-2" notice={closedParentNotice} />
            {renderHeader()}
            {renderSubTickets()}
        </div>
    );
};

export default TaskSubTickets;
